import { supabase } from "../supabaseClient";

export const TESTER_ACTIVITY_ACTIONS = {
  APP_OPEN: "app_open",
  RECOMMENDATION: "recommendation",
  PLACE_VIEW: "place_view",
  REVIEW: "review",
  DON_D_MESSAGE: "don_d_message",
  ROUTE_INTENT: "route_intent",
  APP_RETURN: "app_return",
  CLIENT_ERROR: "client_error",
};

const isDev = process.env.NODE_ENV !== "production";
const lastActionKeys = new Map();
const SESSION_KEY = "venaqqi_tester_client_session_v1";
const SAFE_CONTEXT_KEYS = new Set(["source", "place_id", "function", "severity", "error_code", "route_state"]);

const randomId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `vq-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const clientSessionId = () => {
  if (typeof window === "undefined") return randomId();
  try {
    const existing = window.sessionStorage?.getItem(SESSION_KEY);
    if (existing && existing.length >= 8 && existing.length <= 100) return existing;
    const created = randomId().slice(0, 100);
    window.sessionStorage?.setItem(SESSION_KEY, created);
    return created;
  } catch {
    return randomId().slice(0, 100);
  }
};

const compactText = (value, max = 120) => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(/\s+/g, " ");
  return normalized ? normalized.slice(0, max) : null;
};

const safeCallerContext = value => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => SAFE_CONTEXT_KEYS.has(key))
    .map(([key, raw]) => {
      if (key === "place_id") {
        const n = Number(raw);
        return [key, Number.isFinite(n) ? n : null];
      }
      return [key, compactText(raw, key === "error_code" ? 80 : 120)];
    })
    .filter(([, normalized]) => normalized !== null));
};

export const testerRuntimeContext = (extra = {}) => {
  const win = typeof window !== "undefined" ? window : null;
  const doc = typeof document !== "undefined" ? document : null;
  const width = Number(win?.innerWidth || 0);
  const deviceClass = width > 0 ? (width <= 480 ? "compact" : width <= 900 ? "medium" : "wide") : "unknown";
  const standalone = Boolean(win?.matchMedia?.("(display-mode: standalone)")?.matches || win?.navigator?.standalone === true);
  return {
    client_session_id: clientSessionId(),
    path: compactText(win?.location?.pathname || "/", 300),
    device_class: deviceClass,
    display_mode: standalone ? "standalone" : "browser",
    visibility: compactText(doc?.visibilityState || "unknown", 30),
    ...safeCallerContext(extra),
  };
};

export const trackTesterActivity = async (action, options = {}) => {
  if (!Object.values(TESTER_ACTIVITY_ACTIONS).includes(action)) return false;
  // The RPC is granted only to authenticated users. Requiring the current app
  // user id prevents guest interactions from producing expected authorization
  // errors and keeps anonymous sessions out of tester telemetry.
  if (!options.userId) return false;

  const dedupeKey = options.dedupeKey || action;
  const dedupeMs = options.dedupeMs ?? (action === TESTER_ACTIVITY_ACTIONS.APP_OPEN ? 60000 : 1500);
  const now = Date.now();
  const previous = lastActionKeys.get(dedupeKey) || 0;
  if (now - previous < dedupeMs) return false;
  lastActionKeys.set(dedupeKey, now);

  try {
    const { error } = await supabase.rpc("track_tester_activity", {
      p_action: action,
      p_context: testerRuntimeContext(options.context),
    });
    if (error) {
      console.error("track_tester_activity RPC failed:", error);
      if (options.throwOnError) throw error;
      if (isDev) console.warn("Tester activity not tracked:", error.message);
    }
    return !error;
  } catch (error) {
    console.error("Tester activity failed:", error);
    if (options.throwOnError) throw error;
    if (isDev) console.warn("Tester activity failed:", error?.message || error);
    return false;
  }
};
